/**
 * AccountChip — a tappable account name with a local alias.
 *
 * EOS account names are unreadable by design (`boomer.pcash`), so the wallet
 * remembers a human name per account and shows "Паша (boomer.pcash)". The alias
 * lives only on this device (persisted app store) — nothing is published on chain.
 *
 * Tapping opens the actions available for somebody else's account: write, send
 * tokens, rename, open in a block explorer, copy.
 */

import { useEffect, useRef, useState } from 'react'
import { getExplorerUrl } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'

/** "Паша (boomer.pcash)" when named, plain account otherwise. */
export function useAccountLabel(account: string): string {
  const alias = useAppStore(s => s.contactNames[account])
  return alias ? `${alias} (${account})` : account
}

export default function AccountChip({
  account,
  className = '',
  style,
}: {
  account: string
  className?: string
  style?: React.CSSProperties
}) {
  const { t } = useTranslation()
  const { activeAccount, navigate, setChatPeer, setTransferRecipient } = useWalletStore()
  const { contactNames, setContactName, setDiscoveryUrl, selectedExplorer } = useAppStore()
  const [open, setOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [copied, setCopied] = useState(false)
  const wrapRef = useRef<HTMLSpanElement | null>(null)

  const alias = contactNames[account]
  const label = alias ? `${alias} (${account})` : account
  const isSelf = activeAccount?.name === account

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setRenaming(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Contract accounts (swap.pcash, eosio.fees, …) are not people — no chat for them.
  const looksLikeContract = /^(eosio|.*\.(fees|ram|rex|stake|token|bpay|vpay|names|saving|msig|wrap))$/.test(
    account,
  )

  const close = () => {
    setOpen(false)
    setRenaming(false)
  }

  const write = () => {
    close()
    setChatPeer(account)
    navigate('messages')
  }

  const send = () => {
    close()
    setTransferRecipient(account)
    navigate('transfer')
  }

  const explore = () => {
    close()
    setDiscoveryUrl(getExplorerUrl(selectedExplorer, 'account', account))
    navigate('discovery')
  }

  const copy = async () => {
    close()
    try {
      await navigator.clipboard.writeText(account)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable */
    }
  }

  const saveName = () => {
    setContactName(account, nameDraft)
    close()
  }

  if (isSelf) {
    return (
      <span className={className} style={style}>
        {label}
      </span>
    )
  }

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={className}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          textDecorationLine: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: 3,
          textDecorationColor: 'rgba(92,191,99,0.5)',
          ...style,
        }}
      >
        {copied ? t('common.copied') : label}
      </button>

      {open && (
        <span
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            zIndex: 40,
            minWidth: 196,
            borderRadius: 12,
            background: '#141914',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 10px 28px rgba(0,0,0,0.55)',
            padding: 4,
            display: 'block',
          }}
        >
          <span
            className="block truncate"
            style={{ padding: '6px 10px', fontSize: 11, color: '#6b7280' }}
          >
            {account}
          </span>

          {renaming ? (
            <span className="block px-1.5 pb-1.5">
              <input
                className="input-field w-full"
                style={{ height: 34, fontSize: 13 }}
                autoFocus
                maxLength={40}
                placeholder={t('accountChip.namePlaceholder')}
                value={nameDraft}
                onChange={e => setNameDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveName()
                  if (e.key === 'Escape') setRenaming(false)
                }}
              />
              <span className="flex gap-1.5 mt-1.5">
                <button
                  type="button"
                  className="btn-primary flex-1"
                  style={{ height: 30, fontSize: 12 }}
                  onClick={saveName}
                >
                  {t('common.save')}
                </button>
                {alias && (
                  <button
                    type="button"
                    className="btn-ghost"
                    style={{ height: 30, fontSize: 12, padding: '0 10px' }}
                    onClick={() => {
                      setContactName(account, '')
                      close()
                    }}
                  >
                    {t('accountChip.clearName')}
                  </button>
                )}
              </span>
            </span>
          ) : (
            <>
              {!looksLikeContract && <MenuItem label={t('accountChip.write')} onClick={write} />}
              <MenuItem label={t('accountChip.send')} onClick={send} />
              <MenuItem
                label={alias ? t('accountChip.rename') : t('accountChip.addName')}
                onClick={() => {
                  setNameDraft(alias ?? '')
                  setRenaming(true)
                }}
              />
              <MenuItem label={t('accountChip.explorer')} onClick={explore} />
              <MenuItem label={t('accountChip.copy')} onClick={() => void copy()} />
            </>
          )}
        </span>
      )}
    </span>
  )
}

function MenuItem({ label, onClick }: { label: string; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left"
      style={{
        padding: '8px 10px',
        fontSize: 13,
        color: '#e5e7eb',
        background: 'none',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}
